import { EMPTY_RANGE_PAD, HEADER_H, MIN_PX_PER_DAY, OVERSCAN_DAYS, PX_PER_DAY, RANGE_PAD_AFTER, RANGE_PAD_BEFORE, ROW_H, TRAILING_LABEL_PX, type ZoomLevel } from './constants'
import { addDays, diffDays, maxIso, minIso, type ISODate } from './format'

export interface RangeInput { scheduleStart: ISODate; today: ISODate; dates: (ISODate | null | undefined)[] }
export interface DateRange { start: ISODate; end: ISODate; totalDays: number }

export const computeRange = ({ scheduleStart, today, dates }: RangeInput): DateRange => {
  const all = dates.filter((d): d is ISODate => !!d)
  let start: ISODate
  let end: ISODate
  if (all.length === 0) {
    start = addDays(today, -EMPTY_RANGE_PAD)
    end = addDays(today, EMPTY_RANGE_PAD)
  } else {
    start = addDays(minIso([scheduleStart, today, ...all]), -RANGE_PAD_BEFORE)
    end = addDays(maxIso([scheduleStart, today, ...all]), RANGE_PAD_AFTER)
  }
  return { start, end, totalDays: diffDays(start, end) + 1 }
}

/** Whole-project zoom fits the range plus the trailing-label gutter into the viewport (Freelo #32277855). */
export const pxPerDayFor = (zoom: ZoomLevel, totalDays: number, viewportWidth: number): number =>
  zoom === 'project'
    ? Math.max(MIN_PX_PER_DAY, Math.max(0, viewportWidth - TRAILING_LABEL_PX) / Math.max(1, totalDays))
    : PX_PER_DAY[zoom]

/** Days the header ticks and gridlines extend to: the range plus the gutter expressed in days, so
 * the space after the last bar still shows dates instead of a blank strip. */
export const timelineDays = (totalDays: number, pxPerDay: number): number =>
  totalDays + Math.ceil(TRAILING_LABEL_PX / pxPerDay)

/** Full drawable width of the timeline pane: range + gutter, never narrower than the viewport. */
export const timelineWidthFor = (totalDays: number, pxPerDay: number, viewportWidth: number): number =>
  Math.max(totalDays * pxPerDay + TRAILING_LABEL_PX, viewportWidth)

export const dayIndex = (date: ISODate, rangeStart: ISODate): number => diffDays(rangeStart, date)
export const dateAt = (day: number, rangeStart: ISODate): ISODate => addDays(rangeStart, Math.floor(day))
export const xOf = (date: ISODate, rangeStart: ISODate, pxPerDay: number): number => dayIndex(date, rangeStart) * pxPerDay

export interface BarBox { x: number; width: number }
/** Finish is inclusive: a one-day task is one day wide. Milestones are a point at the middle of their day. */
export const barBox = (a: { start: ISODate; finish: ISODate; isMilestone: boolean }, rangeStart: ISODate, pxPerDay: number): BarBox => {
  const x = xOf(a.start, rangeStart, pxPerDay)
  if (a.isMilestone) return { x: x + pxPerDay / 2, width: 0 }
  return { x, width: (dayIndex(a.finish, rangeStart) - dayIndex(a.start, rangeStart) + 1) * pxPerDay }
}

/** Top of a visible row in body coordinates (the header sits above row 0). */
export const rowY = (visibleIndex: number): number => HEADER_H + visibleIndex * ROW_H
export const rowCenterY = (visibleIndex: number): number => rowY(visibleIndex) + ROW_H / 2

export const visibleDayWindow = (scrollLeft: number, viewportWidth: number, pxPerDay: number, totalDays: number): { fromDay: number; toDay: number } => ({
  fromDay: Math.max(0, Math.floor(scrollLeft / pxPerDay) - OVERSCAN_DAYS),
  toDay: Math.min(totalDays - 1, Math.ceil((scrollLeft + viewportWidth) / pxPerDay) + OVERSCAN_DAYS),
})

export const centreDate = (scrollLeft: number, viewportWidth: number, pxPerDay: number, rangeStart: ISODate): ISODate =>
  dateAt((scrollLeft + viewportWidth / 2) / pxPerDay, rangeStart)

/** scrollLeft that puts `date` at `fraction` of the viewport width (0.5 = centre, 0.2 = near the left edge). */
export const scrollLeftFor = (date: ISODate, viewportWidth: number, pxPerDay: number, rangeStart: ISODate, fraction = 0.5): number =>
  Math.max(0, Math.round(xOf(date, rangeStart, pxPerDay) - viewportWidth * fraction))
